/**
 * Meta (Facebook) Pixel standard events.
 *
 * The pixel snippet itself is per-tenant config, pasted in FMS → Website
 * Settings and injected by `analytics/marketingTags`, so `fbq` exists only on
 * the storefronts whose operator added one. Everywhere else every call here
 * is a no-op.
 *
 * Why these fire from code rather than Meta's Event Setup Tool: that tool
 * attaches an event to a DOM click it finds when it scans the page, and the
 * checkout button fails both halves of that. It is rendered only after the
 * payment config (`ourCompany.default_payment`, then Stripe availability)
 * resolves, so it is usually not in the DOM when the tool scans; and a click
 * on it is not a payment — a declined card would still be counted as a
 * Purchase, with no value attached. Mirroring the funnel events we already
 * send means Meta sees the payment, with its amount, when it actually
 * succeeds.
 */

let activePixelId = "";

/** The pixel id we initialised from `meta_pixel_id`, or "" when none is set. */
export const getMetaPixelId = (): string => activePixelId;
export const setMetaPixelId = (id: string): void => {
  activePixelId = id;
};

let lastUserDataKey = "";

/**
 * Advanced matching (1NOW-11118): re-`init` the pixel with hashed-by-Meta
 * identifiers. Values go only to fbq, never to URLs or PostHog. Skipped when
 * there is no pixel id or the data has not changed.
 */
export function setMetaUserData(user: {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
}): void {
  const fbq = (window as any).fbq;
  if (!activePixelId || typeof fbq !== "function") return;
  const lower = (v?: string) => (v || "").trim().toLowerCase();
  const data: Record<string, string> = {
    em: lower(user.email),
    // react-phone-input-2 already yields digits with the country code
    ph: (user.phone || "").replace(/\D/g, ""),
    fn: lower(user.firstName),
    ln: lower(user.lastName),
  };
  Object.keys(data).forEach((k) => data[k] || delete data[k]);
  const key = activePixelId + JSON.stringify(data);
  if (!Object.keys(data).length || key === lastUserDataKey) return;
  try {
    fbq("init", activePixelId, data);
    lastUserDataKey = key;
  } catch (error) {
    console.error("[Meta] advanced matching failed", error);
  }
}

/** Our funnel event -> Meta standard event. Anything unmapped is not sent. */
const STANDARD_EVENTS: Record<string, string> = {
  vehicle_listing_viewed: "ViewContent",
  booking_started: "InitiateCheckout",
  booking_completed: "Purchase",
  vehicle_search: "Search",
  contact_clicked: "Contact",
};

const ID_PREFIX: Record<string, string> = {
  InitiateCheckout: "ic",
  Purchase: "purchase",
};

/**
 * 1NOW-11119: Meta dedupes browser vs server events on `eventID`. The booking
 * rows use `ic-<id>` / `purchase-<id>`, which the backend can reproduce.
 */
const eventId = (standard: string, bookingId: unknown): string => {
  const prefix = ID_PREFIX[standard];
  if (prefix && bookingId !== undefined && bookingId !== null && bookingId !== "")
    return `${prefix}-${bookingId}`;
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * 1NOW-11121: Meta `Search` carries the trip's dates as `content_category`,
 * "2026-10-20 to 2026-10-23" (pick-up date to return date), beside
 * `search_string` (the pick-up location). Takes the payload's own
 * "2026-10-20T10:00:00" strings, so it is the date the availability API got.
 */
export const searchDatesCategory = (start?: string | null, end?: string | null): string => {
  const day = (iso?: string | null) => String(iso ?? "").split("T")[0];
  return day(start) && day(end) ? `${day(start)} to ${day(end)}` : "";
};

export function trackMetaEvent(
  eventName: string,
  properties?: Record<string, any>
): void {
  const standard = STANDARD_EVENTS[eventName];
  const fbq = (window as any).fbq;
  if (!standard || typeof fbq !== "function") return;

  const props = properties ?? {};
  const value = Number(props.value);
  const payload: Record<string, any> = {};

  if (props.vehicle_id) {
    payload.content_type = "product";
    payload.content_ids = [String(props.vehicle_id)];
  }
  if (props.vehicle_name) payload.content_name = props.vehicle_name;
  if (props.search_string) payload.search_string = String(props.search_string);
  if (props.content_category) payload.content_category = String(props.content_category);
  // A missing or unusable amount still sends the event — Meta counts it, it
  // just cannot optimise on revenue. Sending `value: NaN` would be worse.
  if (Number.isFinite(value) && value > 0) {
    // `value` arrives as whatever the API sent — the price fields are DRF
    // decimals, i.e. strings like "394.00" — and `currency` is lowercase
    // ("usd") on /api/company/configurations/, where Meta wants ISO 4217.
    payload.value = value;
    payload.currency = String(props.currency || "USD").toUpperCase();
  }

  // `fbq` is the operator's own pasted snippet, and this runs on the payment
  // success path — the Purchase call sits between a charged card and the
  // redirect to /payment-success. A broken vendor snippet must cost us an
  // analytics event, never the renter's confirmation.
  try {
    fbq("track", standard, payload, {
      eventID: eventId(standard, props.booking_id),
    });
  } catch (error) {
    console.error("[Meta] pixel event failed", standard, error);
  }
}
